import React, { ReactNode, useCallback, useEffect, useMemo } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Alert, InlineSelectField } from "@mapmycustomers/ui";

import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { getFieldCategoryDisplayName } from "@app/util/ui";

import {
  isFieldSupportedByColorLegend,
  isFieldSupportedByShapeLegend,
} from "../utils/pinLegendFieldFilters";

import styles from "./FieldNameSelectField.module.scss";

interface Props {
  creating: boolean;
  entityType: EntityTypesSupportedByMapsPage;
  label?: ReactNode;
  onChange?: (fieldName?: string) => void;
  type: PinLegendType;
  value?: string;
}

const FieldNameSelectField: React.FC<Props> = ({
  creating,
  entityType,
  label,
  onChange,
  type,
  value,
}) => {
  const intl = useIntl();

  const fieldModel = useMemo(() => getFieldModelByEntityType(entityType), [entityType]);
  const field = useMemo(() => fieldModel.getByName(value ?? ""), [fieldModel, value]);

  useEffect(() => {
    // reset field name if such field doesn't exist. Mostly useful when user changes entity type selection
    if (!field && onChange) {
      onChange(undefined);
    }
  }, [field, onChange]);

  const valueRender = useCallback(
    (value: IField["name"] | null | undefined) => {
      if (!value) {
        return "";
      }
      return field?.displayName ?? "";
    },
    [field]
  );

  return (
    <div className={styles.container}>
      <InlineSelectField<IField["name"]>
        className={styles.fieldSelect}
        disabled={!creating}
        dropdownMatchSelectWidth={false}
        label={label}
        onChange={onChange}
        optionFilterProp="label"
        showFooterButtons={false}
        showSearch
        suffixIcon={creating ? undefined : <FontAwesomeIcon icon={faLock} />}
        value={field?.name}
        valueRender={valueRender}
      >
        {Object.entries(fieldModel.categorizedFields).map(([category, allFields]) => {
          const fields = allFields.filter(
            type === PinLegendType.COLOR
              ? isFieldSupportedByColorLegend
              : isFieldSupportedByShapeLegend
          );
          return !fields.length ? null : (
            <Select.OptGroup
              key={category}
              label={getFieldCategoryDisplayName(intl, category as FieldCategory)}
            >
              {fields.map((field) => (
                <Select.Option key={field.name} label={field.displayName} value={field.name}>
                  {field.displayName}
                </Select.Option>
              ))}
            </Select.OptGroup>
          );
        })}
      </InlineSelectField>
      {creating && (
        <Alert
          message={
            type === PinLegendType.COLOR
              ? intl.formatMessage(
                  {
                    id: "map.colorization.createEditSidebar.detailsForm.fieldName.alert.color",
                    defaultMessage:
                      "Please note that coloring pins by text, time, and address fields is <b>not supported</b>. You may only color by single/multiple option fields, date fields, numeric fields, and monetary fields.",
                    description:
                      "Alert about supported coloring fields in Create Pin Legend drawer on the Map page",
                  },
                  {
                    b: (texts: string[]) => <b>{texts}</b>,
                  }
                )
              : intl.formatMessage(
                  {
                    id: "map.colorization.createEditSidebar.detailsForm.fieldName.alert.shape",
                    defaultMessage:
                      "Please note that shaping pins by text, time, and address fields is <b>not supported</b>. You may only set shape for single/multiple option fields, date fields, numeric fields, and monetary fields.",
                    description:
                      "Alert about supported shape fields in Create Pin Legend drawer on the Map page",
                  },
                  {
                    b: (texts: string[]) => <b>{texts}</b>,
                  }
                )
          }
          type="info"
        />
      )}
    </div>
  );
};

export default FieldNameSelectField;
